import { styled } from "@linaria/react"
import { Link, useNavigate } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useCallback, useState } from "react"


import { Avatar, AvatarWrapper } from "@/components/Avatar/Avatar.tsx"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading3 } from "@/components/Heading/Heading.tsx"
import { Loading } from "@/components/Loading.tsx"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Toggle } from "@/components/Toggle/Toggle.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { isDevModeAllowedForProfile, setDevMode, useDevMode } from "@/hooks/useDevMode.ts"
import { useModel } from "@/hooks/useModel.ts"
import { getFullName, useMyProfile } from "@/hooks/useProfile.ts"
import { useInvalidateQueries, useOrgContext, useQueries } from "@/hooks/useRootContext.ts"
import {
  CreditsRegular,
  DiscordFill,
  LogoutRegular,
  OpenInNewTabRegular,
  SettingsRegular,
  SwitchWorkspaceRegular
} from "@/icons/Icons.ts"
import { formatDateTime } from "@/lib/date.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"
import { InviteTeammatesButton, InviteTeammatesButtonIcon } from "@/routes/auth/InviteTeammatesButton.ts"
import { InviteTeammatesDialog } from "@/routes/auth/InviteTeammatesDialog.tsx"
import { UserTaskCounts } from "@/routes/auth/UserTaskCounts.tsx"
import { Navigator } from "@/routes/Navigator.ts"
import { useOrganizationPermissions } from "@/routes/projects/projectHooks.ts"
import { OrgQuotaModel } from "@/routes/user/OrgQuotaModel.ts"
import { UserSettingsDialog } from "@/routes/user/UserSettingsDialog.tsx"
import { TabId } from "@/routes/user/UserSettingsTabId.ts"
import { ellipsis } from "@/styles/mixins.ts"
import { ellipsize, smallFontSize } from "@/styles/textStyles.ts"

import type { AppDateTime } from "@/lib/client/api-types.ts"
import type { TD_MemberProfile } from "@/lib/client/generated/TypeDefinitions.ts"

export const UserMenu = () => {
  const myProfile = useMyProfile()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  if (!myProfile) return null

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <UserMenuTrigger data-testid="user-settings">
        <Avatar profile={myProfile} size={40} />
      </UserMenuTrigger>
      <AvatarShadow />
      {dropdownOpen && (
        <Loading renderLoading={
          <DropdownMenuContent align="end"><Spinner text={ellipsize("Loading")} /></DropdownMenuContent>
        }>
          <UserMenuDropdown profile={myProfile} setDropdownOpen={setDropdownOpen} />
        </Loading>
      )}
    </DropdownMenu>
  )
}

const UserMenuDropdown = observer((props: {
  profile: TD_MemberProfile
  setDropdownOpen: (open: boolean) => void
}) => {
  const context = useOrgContext()
  const permissions = useOrganizationPermissions()
  const navigate = useNavigate()

  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [inviteTeammatesDialogOpen, setInviteTeammatesDialogOpen] = useState(false)
  const [confirmLogoutDialogOpen, setConfirmLogoutDialogOpen] = useState(false)

  const logout = useCallback(async () => {
    context.persistence.clear()
    await context.authController.logout()
    startViewTransition(() => navigate(Navigator.login({ replace: true })))
  }, [context.authController, context.persistence, navigate])

  const openDiscord = () => {
    openLinkInNewWindow("https://discord.com/invite/9Ga49Tw69G")
  }

  const devMode = useDevMode()

  const queries = useQueries()
  const invalidateQueries = useInvalidateQueries()

  const toggleDevMode = useCallback(() => {
    void setDevMode(queries, invalidateQueries, !devMode)
  }, [queries, invalidateQueries, devMode])

  const isDevModeAllowed = isDevModeAllowedForProfile(props.profile)

  const orgQuotaModel = useModel(() => new OrgQuotaModel(context))

  const orgQuotaUntil = orgQuotaModel.quota.get()?.until ?? null

  return (
    <UserMenuDropdownMenuContent data-testid="user-menu-dropdown" side="top" align="start">
      <UserMenuDropdownMenuItem disabled data-testid="user-menu-item-header">
        <Vertical gap="4px">
          <EllipsisContainer>
            <UserName>
              {getFullName(props.profile)}
            </UserName>
          </EllipsisContainer>

          <UserTaskCounts profile={props.profile} />
        </Vertical>
      </UserMenuDropdownMenuItem>

      {isDevModeAllowed && (
        <>
          <UserMenuDropdownMenuItem disabled data-testid="user-menu-item-dev-mode" onClick={(e) => {
            e.preventDefault()
            toggleDevMode()
          }}>
            <Horizontal alignItems="center" gap="8px">
              <Toggle active={devMode} />
              Dev mode
            </Horizontal>
          </UserMenuDropdownMenuItem>

          <MenuSeparator />
        </>
      )}

      <Tooltip
        side="right"
        content={orgQuotaUntil ? <CreditsTooltipContent until={orgQuotaUntil} /> : null}
        ignoreEllipsis
      >
        <UserMenuDropdownMenuItem disabled linkInside data-testid="user-menu-item-credits" onClick={() => {
          // TODO: Implement credits functionality
        }}>
          <CreditsContainer>
            <Horizontal alignItems="center" gap="8px">
              <UserMenuIcon>
                <CreditsRegular />
              </UserMenuIcon>
              Credits
            </Horizontal>
            <QuotaInfoInline model={orgQuotaModel} />
          </CreditsContainer>
        </UserMenuDropdownMenuItem>
      </Tooltip>

      <UserMenuDropdownMenuItem
        disabled
        linkInside
        onClick={() => setSettingsDialogOpen(true)}
        data-testid="user-menu-item-settings"
      >
        <SettingsLink
          {...Navigator.settings(context.orgId, TabId.Profile)}
          onClick={(e) => e.preventDefault()}
        >
          <Horizontal alignItems="center" gap="8px">
            <UserMenuIcon>
              <SettingsRegular />
            </UserMenuIcon>
            Settings
          </Horizontal>
        </SettingsLink>
      </UserMenuDropdownMenuItem>

      <UserSettingsDialog dialogOpen={settingsDialogOpen} setDialogOpen={setSettingsDialogOpen} />

      <UserMenuDropdownMenuItem disabled linkInside data-testid="user-menu-item-join-community" onClick={openDiscord}>
        <JoinCommunityContainer>
          <Horizontal alignItems="center" gap="8px">
            <UserMenuIcon>
              <DiscordFill />
            </UserMenuIcon>
            Join community
          </Horizontal>
          <UserMenuIcon>
            <OpenInNewTabRegular />
          </UserMenuIcon>
        </JoinCommunityContainer>
      </UserMenuDropdownMenuItem>

      {permissions.canInviteUsers && (
        <>
          <UserMenuDropdownMenuItem data-testid="user-menu-item-invite-teammates" disabled onClick={(e) => {
            e.preventDefault()
            setInviteTeammatesDialogOpen(true)
          }}>
            <InviteTeammatesButtonFullWidth>
              <InviteTeammatesButtonIcon />
              Invite teammates
            </InviteTeammatesButtonFullWidth>
          </UserMenuDropdownMenuItem>

          <InviteTeammatesDialog dialogOpen={inviteTeammatesDialogOpen} setDialogOpen={setInviteTeammatesDialogOpen} />
        </>
      )}

      <MenuSeparator />

      <UserMenuDropdownMenuItem disabled linkInside data-testid="user-menu-item-switch-workspace">
        <SwitchWorkspaceLink {...Navigator.home()} viewTransition>
          <Horizontal alignItems="center" gap="8px">
            <UserMenuIcon>
              <SwitchWorkspaceRegular />
            </UserMenuIcon>
            Switch workspace
          </Horizontal>
        </SwitchWorkspaceLink>
      </UserMenuDropdownMenuItem>

      <ConfirmDialog
        open={confirmLogoutDialogOpen}
        onOpenChange={setConfirmLogoutDialogOpen}
        trigger={
          <LogoutMenuItem disabled linkInside data-testid="user-menu-item-logout" onClick={(e) => {
            e.preventDefault()
            setConfirmLogoutDialogOpen(true)
          }}>
            <Horizontal alignItems="center" gap="8px">
              <UserMenuIcon>
                <LogoutRegular />
              </UserMenuIcon>
              Log out
            </Horizontal>
          </LogoutMenuItem>
        }
        description="Are you sure you want to log out?"
        confirmText="Log out"
        onConfirm={logout}
      />
    </UserMenuDropdownMenuContent>
  )
})

export const QuotaInfoInline = observer(({ model }: { model: OrgQuotaModel }) => {
    if (model.isLoading.get()) {
      return <QuotaSpan>{ellipsize("Loading")}</QuotaSpan>
    }

    const quota = model.quota.get()

    if (!quota) {
      return null
    }

    const current = parseInt(quota.current)
    const maximum = parseInt(quota.maximum)

    let divisor = 1
    if (maximum >= 1_000_000_000) {
      divisor = 1_000_000
    } else if (maximum >= 1_000_000) {
      divisor = 1_000
    }

    const scaledCurrent = Math.round(current / divisor)
    const scaledMaximum = Math.round(maximum / divisor)

    return (
      <QuotaSpan>{scaledCurrent} / {scaledMaximum}</QuotaSpan>
    )
  }
)

const CreditsTooltipContent = observer(({ until }: { until: AppDateTime }) => {
  return `Next renewal date: ${formatDateTime(until)}`
})

const AvatarShadow = styled.div`
  width: 36px;
  height: 12px;
  margin-top: -16px;

  background: radial-gradient(50% 50% at 50% 50%, var(--text-color) 0%, var(--background-color) 100%); /* TODO dynamic shadow color */

  filter: blur(3px);
  opacity: 0.3;
`

const UserMenuTrigger = styled(DropdownMenuTrigger)`
  border-radius: var(--border-radius-round);

  ${AvatarWrapper} {
    z-index: -1;
  }
`

const UserMenuDropdownMenuContent = styled(DropdownMenuContent)`
  gap: 8px;

  min-width: 320px;
  max-width: 400px;
  padding: 24px;

  border-radius: var(--border-radius-32);
`

const UserMenuDropdownMenuItem = styled(DropdownMenuItem)<{
  linkInside?: boolean
}>`
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 0;

  ${smallFontSize};

  font-weight: 500;

  &:hover {
    color: ${props => props.linkInside === true ? "var(--main-color)" : "inherit"};
    cursor: ${props => props.linkInside === true ? "pointer" : "default"};
  }
`

const UserName = styled(Heading3)`
  ${ellipsis};
`

const UserMenuIcon = styled(Vertical)`
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
`

const SettingsLink = styled(Link)`
  width: 100%;

  color: inherit;

  &:hover {
    color: inherit;
  }
`

const SwitchWorkspaceLink = styled(Link)`
  width: 100%;

  color: inherit;

  &:hover {
    color: inherit;
  }
`

const LogoutMenuItem = styled(UserMenuDropdownMenuItem)`
  padding-top: 8px;
`

const JoinCommunityContainer = styled(Horizontal)`
  justify-content: space-between;
  align-items: center;

  width: 100%;

  & > ${UserMenuIcon}:last-child {
    color: var(--secondary-text-color);
  }
`

const MenuSeparator = styled.div`
  align-self: stretch;

  height: 1px;
  margin: 0 -24px;

  background-color: var(--separator-color);
`

const CreditsContainer = styled(Horizontal)`
  justify-content: space-between;
  align-items: center;

  width: 100%;
`

const QuotaSpan = styled(SecondaryTextSpan)`
  ${smallFontSize};
`

const InviteTeammatesButtonFullWidth = styled(InviteTeammatesButton)`
  width: 100%;
  margin: 8px 0;
`
